import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext';
import { StreakXpBadge } from '../ds';
import { WebSidebar } from './WebSidebar';
import { WebBottomNav } from './WebBottomNav';
import { WebTopBar } from './WebTopBar';
import { WebFocus } from './WebFocus';
import { WebPanel } from './WebPanel';
import { WebUnitDialog } from './WebUnitDialog';
import { WebExerciseDialog } from './WebExerciseDialog';
import { NAV_PATH, PATH_NAV, WEB_PAGE_META, type NavKey } from './nav';
import { useViewport } from './useViewport';
import { startSession, type LessonSession } from './lessonFlow';

import { useAsync } from '../hooks/useAsync';
import { contentApi } from '../api/content';
import { progressApi } from '../api/progress';
import { isAiFeatureEnabled, isSocialFeatureEnabled } from '../featureFlags';
import { ChatRealtimeProvider, useChatRealtime } from '../chat/ChatRealtimeContext';
import { SocialProvider } from '../screens/social/SocialContext';
import type { LessonSummary, UnitDetail, UnitSummary } from '../api/content.types';

import { WebPathScreen } from '../pages/WebPathScreen';
import { WebReviewScreen } from '../pages/WebReviewScreen';
import { WebAIScreen } from '../pages/WebAIScreen';
import { WebCommunityPage } from '../pages/WebCommunityPage';
import { WebChatPage } from '../pages/WebChatPage';
import { WebProfileScreen } from '../pages/WebProfileScreen';
import { FlashcardLibraryPage } from '../pages/FlashcardLibraryPage';
import { UnitItemListPage } from '../pages/UnitItemListPage';
import { KanaLibraryPage } from '../pages/KanaLibraryPage';
import { PlaceholderPage } from '../pages/PlaceholderPage';

import { DailyGoalModal } from '../screens/path/DailyGoalModal';
import { KanaIntroScreen } from '../screens/lesson/KanaIntroScreen';
import { KanaQuizScreen } from '../screens/lesson/KanaQuizScreen';
import { VocabIntroScreen } from '../screens/lesson/VocabIntroScreen';
import { VocabQuizScreen } from '../screens/lesson/VocabQuizScreen';
import { GrammarLessonScreen } from '../screens/lesson/GrammarLessonScreen';
import { PracticeScreen } from '../screens/lesson/PracticeScreen';
import { CompleteScreen } from '../screens/lesson/CompleteScreen';
import { ExerciseQuizScreen } from '../screens/exercise/ExerciseQuizScreen';
import { MockTestScreen } from '../screens/mocktest/MockTestScreen';
import { srsApi } from '../api/srs';
import type { RatingIntervals, ReviewRating } from '../api/srs.types';
import { SrsReviewScreen } from '../screens/srs/SrsReviewScreen';
import { SrsSettingsScreen } from '../screens/srs/SrsSettingsScreen';
import { FlashcardDeckView } from '../screens/library/FlashcardDeckView';
import { findKanaGroup } from '../screens/library/kanaLibraryData';
import { fetchLessonItemsForMode } from '../screens/library/lessonItems';
import type { FlashcardMode, KanaGroupKey } from '../screens/library/types';
import { AccountSettingsScreen } from '../screens/settings/AccountSettingsScreen';
import { EditProfileScreen } from '../screens/settings/EditProfileScreen';
import { AboutLicenseScreen, PrivacyPolicyScreen, TermsScreen } from '../screens/settings/LegalScreen';
import { ErrorState, LoadingState } from '../components/ScreenState';
import type { LearningItemView } from '../api/content.types';

/** Bộ thẻ đang mở trong chế độ học tập (flashcard duyệt tự do). */
type DeckTarget =
  | { kind: 'unit'; unitId: string; unitName: string; mode: FlashcardMode }
  | { kind: 'kana'; groupKey: KanaGroupKey; groupTitle: string };

/// Unit "nền tảng" có order 0..2; unit "Bài N" có order = N + BAI_PATH_ORDER_OFFSET —
/// PHẢI khớp hằng số cùng tên ở WebPathScreen.tsx / progress.service.ts / MapScreen.tsx (mobile).
const BAI_PATH_ORDER_OFFSET = 2;

/// Màn rộng đủ cho cột "Thông tin nhóm" thứ ba ở trang Chat.
const CHAT_WIDE_MIN = 1280;

/** Socket chat bọc cả shell: badge tin chưa đọc trên sidebar/thanh tab cần nó ở mọi trang. */
export function WebAppShell() {
  if (!isSocialFeatureEnabled()) return <WebAppShellInner />;
  return (
    <ChatRealtimeProvider>
      <WebAppShellInner />
    </ChatRealtimeProvider>
  );
}

function WebAppShellInner() {
  const { width, collapsed, narrow, phone } = useViewport();
  const navigate = useNavigate();
  const location = useLocation();
  const aiEnabled = isAiFeatureEnabled();
  const socialEnabled = isSocialFeatureEnabled();
  const { unread: chatUnread } = useChatRealtime();
  // Tắt AI (hoặc Cộng đồng/Chat) thì gõ thẳng URL cũng về Học — sidebar/thanh tab đã ẩn mục này, như mobile.
  const pathNav: NavKey = PATH_NAV[location.pathname] ?? 'hoc';
  const hidden = (pathNav === 'ai' && !aiEnabled) || ((pathNav === 'congdong' || pathNav === 'chat') && !socialEnabled);
  const nav: NavKey = hidden ? 'hoc' : pathNav;

  const { user, logout, updateDisplayName, deleteAccount } = useAuth();

  /// Đổi để fetch lại gamification sau khi hoàn thành bài / đổi mục tiêu.
  const [reloadKey, setReloadKey] = useState(0);
  const gamification = useAsync(() => progressApi.getGamification(), [reloadKey]);

  const [session, setSession] = useState<LessonSession | null>(null);
  const [srsOpen, setSrsOpen] = useState(false);
  const [deck, setDeck] = useState<DeckTarget | null>(null);

  const [openUnit, setOpenUnit] = useState<UnitSummary | null>(null);
  const [openTrack, setOpenTrack] = useState<'VOCAB' | 'GRAMMAR' | undefined>(undefined);
  const unitDetail = useAsync<UnitDetail | null>(() => (openUnit ? contentApi.getUnit(openUnit.id) : Promise.resolve(null)), [openUnit?.id]);

  // Node "Bài tập" — WebExerciseDialog tự fetch riêng theo unitId, không qua
  // unitDetail/WebUnitDialog ở trên (khác hẳn lesson list của VOCAB/GRAMMAR).
  const [exerciseUnit, setExerciseUnit] = useState<UnitSummary | null>(null);
  const [exerciseQuiz, setExerciseQuiz] = useState<{ unitId: string; baiNumber: number; setNumber: number; lo: number; hi: number } | null>(
    null,
  );

  // "Bài test" sau mỗi 5 Bài — chạy trong chế độ học tập như ExerciseQuiz.
  const [mockTest, setMockTest] = useState<{ id: string; title: string } | null>(null);
  /// Còn bài test đang làm chưa nộp — thoát chế độ học tập thì hỏi lại trước.
  const mockTestDirty = useRef(false);

  const [goalModal, setGoalModal] = useState(false);
  // Override cục bộ sau khi lưu (optimistic) — trước đó fallback vào dữ liệu vừa fetch.
  const [savedGoals, setSavedGoals] = useState<{ vocab: number; grammar: number } | null>(null);
  const goals = savedGoals ?? gamification.data?.dailyGoal ?? { vocab: 20, grammar: 2 };

  const [placeholderTitle, setPlaceholderTitle] = useState('Sắp ra mắt');
  const [deckItems, setDeckItems] = useState<{ unitId: string; unitName: string; mode: FlashcardMode } | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = useCallback((m: string) => {
    setToast(m);
    setTimeout(() => setToast(null), 1800);
  }, []);

  const go = useCallback(
    (key: NavKey) => {
      navigate(NAV_PATH[key]);
    },
    [navigate],
  );

  const exitFocus = useCallback(() => {
    if (mockTestDirty.current && !window.confirm('Thoát bài test? Bài đang làm chưa nộp sẽ không được lưu.')) return;
    // Vừa nộp bài test → thẻ trên Lộ trình (điểm cao nhất) và điểm danh tuần cần tải lại.
    if (mockTest) setReloadKey((k) => k + 1);
    mockTestDirty.current = false;
    setMockTest(null);
    setSession(null);
    setSrsOpen(false);
    setDeck(null);
    setExerciseQuiz(null);
  }, [mockTest]);

  // Khác exitFocus thường: bộ đề vừa NỘP xong (chấm điểm, lưu UserExerciseAnswer)
  // nên phải bump reloadKey để WebPathScreen fetch lại /me/progress — nếu không
  // % node "Bài tập" vẫn hiện số cũ cho tới khi F5 trang. Khớp cách onBackToMap
  // của CompleteScreen đã làm cho các lesson khác.
  const exitExerciseQuiz = useCallback(() => {
    setExerciseQuiz(null);
    setReloadKey((k) => k + 1);
  }, []);

  const focusOpen = session !== null || srsOpen || deck !== null || exerciseQuiz !== null || mockTest !== null;

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (exerciseQuiz) exitExerciseQuiz();
      else if (focusOpen) exitFocus();
      else if (openUnit) setOpenUnit(null);
      else if (exerciseUnit != null) setExerciseUnit(null);
      else if (goalModal) setGoalModal(false);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [focusOpen, openUnit, exerciseUnit, exerciseQuiz, goalModal, exitFocus, exitExerciseQuiz]);

  const startLesson = (unitName: string, lesson: LessonSummary) => {
    setOpenUnit(null);
    setSession(startSession(unitName, lesson));
  };

  const advance = () => setSession((s) => (s ? { ...s, index: Math.min(s.index + 1, s.steps.length - 1) } : s));
  const back = () =>
    setSession((s) => {
      if (!s) return s;
      if (s.index === 0) return null;
      return { ...s, index: s.index - 1 };
    });

  /* ---------- chế độ học tập ---------- */
  function renderFocus() {
    if (srsOpen) {
      return (
        <SrsReviewScreen
          onExit={() => {
            setSrsOpen(false);
            setReloadKey((k) => k + 1);
          }}
          onToast={showToast}
        />
      );
    }

    if (deck) {
      return (
        <DeckFocus
          target={deck}
          onToast={showToast}
          onBack={() => {
            exitFocus();
            // Rating flashcard tạo/dời lịch thẻ SRS → số thẻ đến hạn đã đổi.
            setReloadKey((k) => k + 1);
          }}
        />
      );
    }

    if (mockTest) {
      return (
        <MockTestScreen
          key={mockTest.id}
          mockTestId={mockTest.id}
          title={mockTest.title}
          onExit={exitFocus}
          onDirtyChange={(dirty) => {
            mockTestDirty.current = dirty;
          }}
        />
      );
    }

    if (exerciseQuiz) {
      return (
        <ExerciseQuizScreen
          key={`${exerciseQuiz.unitId}-${exerciseQuiz.setNumber}-${exerciseQuiz.lo}-${exerciseQuiz.hi}`}
          unitId={exerciseQuiz.unitId}
          baiNumber={exerciseQuiz.baiNumber}
          setNumber={exerciseQuiz.setNumber}
          lo={exerciseQuiz.lo}
          hi={exerciseQuiz.hi}
          onBack={exitFocus}
          onDone={exitExerciseQuiz}
        />
      );
    }

    if (session) {
      const step = session.steps[session.index];
      const params = { lessonId: session.lessonId, unitName: session.unitName, lessonName: session.lessonName };
      const common = { ...params, onBack: back, onNext: advance };
      switch (step) {
        case 'kanaIntro':
          return <KanaIntroScreen {...common} />;
        case 'kanaQuiz':
          return <KanaQuizScreen {...common} />;
        case 'vocabIntro':
          return <VocabIntroScreen {...common} />;
        case 'vocabQuiz':
          return <VocabQuizScreen {...common} />;
        case 'grammar':
          return <GrammarLessonScreen {...common} />;
        case 'practice':
          return <PracticeScreen {...common} />;
        case 'complete':
          return (
            <CompleteScreen
              {...params}
              onBackToMap={() => {
                setSession(null);
                setReloadKey((k) => k + 1);
                navigate(NAV_PATH.hoc);
              }}
            />
          );
      }
    }
    return null;
  }

  function focusChrome() {
    // Không hiện số thẻ ở đây: màn SRS tự hiện tiến độ đã-ôn/tổng, còn srsDueCount lấy lúc
    // mở shell có thể đã cũ. Khớp cách mobile dựng header màn ôn tập.
    if (srsOpen) return { label: 'Ôn tập SRS', sub: undefined, width: 620 };
    if (deck) return { label: deck.kind === 'unit' ? deck.unitName : deck.groupTitle, sub: 'Flashcard tự học', width: 620 };
    if (exerciseQuiz) return { label: 'Bài tập', sub: 'Đề dạng JLPT N5', width: 620 };
    if (mockTest) return { label: 'Bài test', sub: `${mockTest.title} · Đề dạng JLPT N5`, width: 640 };
    if (session) return { label: `${session.unitName} · ${session.lessonName}`, sub: undefined, width: 660 };
    return { label: 'Chế độ học tập', sub: undefined, width: 660 };
  }

  /* ---------- các trang web ---------- */
  function renderPage() {
    switch (nav) {
      case 'hoc':
        return (
          <WebPathScreen
            narrow={narrow}
            userName={user?.displayName ?? 'bạn'}
            gamification={gamification.data}
            goals={goals}
            reloadKey={reloadKey}
            onOpenUnit={(unit, track) => {
              if (track === 'PRACTICE') {
                setExerciseUnit(unit);
                return;
              }
              setOpenTrack(track);
              setOpenUnit(unit);
            }}
            onStartReview={() => setSrsOpen(true)}
            onOpenGoal={() => setGoalModal(true)}
            onOpenMockTest={(test) => setMockTest({ id: test.id, title: test.title })}
          />
        );
      case 'ontap':
        return (
          <WebReviewScreen
            narrow={narrow}
            gamification={gamification.data}
            onStart={() => setSrsOpen(true)}
            onSrsSettings={() => go('srsSettings')}
            onOpenKana={() => go('kanaLib')}
            onOpenDecks={() => go('decks')}
          />
        );
      case 'congdong':
        return (
          <SocialProvider onToast={showToast}>
            <WebCommunityPage narrow={narrow} phone={phone} />
          </SocialProvider>
        );
      case 'chat':
        return (
          <SocialProvider onToast={showToast}>
            <WebChatPage narrow={narrow} wide={width >= CHAT_WIDE_MIN} phone={phone} />
          </SocialProvider>
        );
      case 'ai':
        return (
          <WebAIScreen
            narrow={narrow}
            onOpenTool={(title) => {
              setPlaceholderTitle(title);
              go('placeholder');
            }}
          />
        );
      case 'hoso':
        return (
          <WebProfileScreen
            narrow={narrow}
            name={user?.displayName}
            email={user?.email}
            gamification={gamification.data}
            loading={gamification.loading}
            onOpenSettings={() => go('account')}
            onOpenPlaceholder={(title) => {
              setPlaceholderTitle(title);
              go('placeholder');
            }}
            onLogout={logout}
          />
        );
      case 'kanaLib':
        return <KanaLibraryPage narrow={narrow} onStudy={(groupKey, groupTitle) => setDeck({ kind: 'kana', groupKey, groupTitle })} />;
      case 'decks':
        return (
          <FlashcardLibraryPage
            narrow={narrow}
            onOpenUnit={(unitId, unitName, mode) => {
              setDeckItems({ unitId, unitName, mode });
              go('deckItems');
            }}
          />
        );
      case 'deckItems':
        if (!deckItems) return <ErrorState message="Chưa chọn bài nào." onRetry={() => go('decks')} />;
        return (
          <UnitItemListPage
            unitId={deckItems.unitId}
            mode={deckItems.mode}
            onStudy={() => setDeck({ kind: 'unit', unitId: deckItems.unitId, unitName: deckItems.unitName, mode: deckItems.mode })}
          />
        );
      case 'srsSettings':
        return (
          <WebPanel fill={phone} height={560}>
            <SrsSettingsScreen onBack={() => go('ontap')} onToast={showToast} onSrsEnabledSaved={() => setReloadKey((k) => k + 1)} />
          </WebPanel>
        );
      case 'account':
        return (
          <WebPanel fill={phone} height={720}>
            <AccountSettingsScreen
              email={user?.email}
              hasPassword={user?.hasPassword ?? true}
              onBack={() => go('hoso')}
              onEditProfile={() => go('editProfile')}
              onSrsSettings={() => go('srsSettings')}
              onLogout={logout}
              onAbout={() => go('about')}
              onPrivacy={() => go('privacy')}
              onTerms={() => go('terms')}
              onDeleteAccount={deleteAccount}
            />
          </WebPanel>
        );
      case 'editProfile':
        return (
          <WebPanel fill={phone} height={420}>
            <EditProfileScreen
              displayName={user?.displayName ?? null}
              email={user?.email ?? null}
              onBack={() => go('account')}
              onSave={updateDisplayName}
              onToast={showToast}
            />
          </WebPanel>
        );
      case 'about':
        return (
          <WebPanel fill={phone} height={560}>
            <AboutLicenseScreen onBack={() => go('account')} />
          </WebPanel>
        );
      case 'privacy':
        return (
          <WebPanel fill={phone} height={560}>
            <PrivacyPolicyScreen onBack={() => go('account')} />
          </WebPanel>
        );
      case 'terms':
        return (
          <WebPanel fill={phone} height={500}>
            <TermsScreen onBack={() => go('account')} />
          </WebPanel>
        );
      case 'placeholder':
        return <PlaceholderPage title={placeholderTitle} />;
      default:
        return null;
    }
  }

  const meta = WEB_PAGE_META[nav];
  const chrome = focusChrome();
  const subtitle = nav === 'deckItems' && deckItems ? deckItems.unitName : meta.sub;

  const due = gamification.data?.srsEnabled === false ? 0 : (gamification.data?.srsDueCount ?? 0);

  return (
    // 100dvh: trên trình duyệt điện thoại 100vh tính cả phần thanh địa chỉ bị ẩn → đáy trang bị che.
    <div style={{ height: '100dvh', display: 'flex', flexDirection: phone ? 'column' : 'row', background: 'var(--surface-tint)', overflow: 'hidden' }}>
      {!phone && (
        <WebSidebar
          active={nav}
          onNav={go}
          name={user?.displayName}
          email={user?.email}
          aiEnabled={aiEnabled}
          socialEnabled={socialEnabled}
          collapsed={collapsed}
          due={due}
          chatUnread={chatUnread}
        />
      )}

      <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <WebTopBar
          compact={phone}
          title={nav === 'deckItems' && deckItems ? deckItems.unitName : meta.title}
          sub={subtitle}
          onBack={meta.back ? () => go(meta.back as NavKey) : null}
          right={
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              {/* Điện thoại: chỉ streak cho gọn; thanh tab dưới đã có nút về Học. */}
              <StreakXpBadge streak={gamification.data?.streak ?? 0} xp={phone ? undefined : (gamification.data?.xp ?? 0)} />
              {nav !== 'hoc' && !phone && (
                <button
                  onClick={() => go('hoc')}
                  style={{
                    height: 40,
                    padding: '0 16px',
                    borderRadius: 'var(--radius-control)',
                    background: 'var(--cta-default)',
                    color: '#fff',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-vi)',
                    fontWeight: 600,
                    fontSize: 14,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Về lộ trình
                </button>
              )}
            </div>
          }
        />
        {nav === 'chat' ? (
          // Chat tự cuộn trong từng khung (danh sách, tin nhắn) nên trang không cuộn; điện thoại tràn hết màn.
          <main style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: phone ? 0 : narrow ? 20 : '28px 32px' }}>
            <div style={{ flex: 1, minHeight: 0, width: '100%', maxWidth: 1240, margin: '0 auto' }}>{renderPage()}</div>
          </main>
        ) : (
          <main style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: phone ? '16px 16px 32px' : narrow ? '20px 20px 64px' : '28px 32px 72px' }}>
            <div style={{ maxWidth: 1240, margin: '0 auto' }}>{renderPage()}</div>
          </main>
        )}
        {phone && <WebBottomNav active={nav} onNav={go} aiEnabled={aiEnabled} socialEnabled={socialEnabled} due={due} chatUnread={chatUnread} />}
      </div>

      {openUnit && (
        <WebUnitDialog
          unit={unitDetail.data}
          loading={unitDetail.loading}
          error={!!unitDetail.error}
          trackFilter={openTrack}
          onClose={() => setOpenUnit(null)}
          onStartLesson={(lesson) => unitDetail.data && startLesson(unitDetail.data.name, lesson)}
        />
      )}

      {exerciseUnit != null && (
        <WebExerciseDialog
          unitId={exerciseUnit.id}
          baiNumber={exerciseUnit.order - BAI_PATH_ORDER_OFFSET}
          onClose={() => setExerciseUnit(null)}
          onStart={(setNumber, lo, hi) => {
            if (!exerciseUnit) return;
            const unitId = exerciseUnit.id;
            const baiNumber = exerciseUnit.order - BAI_PATH_ORDER_OFFSET;
            setExerciseUnit(null);
            setExerciseQuiz({ unitId, baiNumber, setNumber, lo, hi });
          }}
        />
      )}

      {goalModal && (
        <DailyGoalModal
          goals={goals}
          onClose={() => setGoalModal(false)}
          onSave={(g) => {
            setSavedGoals(g);
            setGoalModal(false);
            progressApi.updateDailyGoal(g).catch(() => showToast('Không lưu được mục tiêu. Thử lại nhé.'));
            showToast('Đã lưu mục tiêu mỗi ngày');
          }}
        />
      )}

      {focusOpen && (
        <WebFocus label={chrome.label} sub={chrome.sub} width={chrome.width} compact={phone} onExit={exitFocus}>
          {renderFocus()}
        </WebFocus>
      )}

      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 28,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 70,
            background: 'var(--ink)',
            color: '#fff',
            fontSize: 13.5,
            fontWeight: 600,
            padding: '12px 22px',
            borderRadius: 999,
            boxShadow: 'var(--shadow-raised)',
            fontFamily: 'var(--font-vi)',
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}

/** Bộ thẻ trong focus mode — tự lo loading/error trước khi dựng FlashcardDeckView. */
function DeckFocus({ target, onBack, onToast }: { target: DeckTarget; onBack: () => void; onToast: (m: string) => void }) {
  // Công tắc "Dùng SRS" đọc lại mỗi lần mở bộ thẻ. srsIntervals=null → duyệt tự do, không gọi /me/srs/*.
  const unitDeck = useAsync<{ items: LearningItemView[]; srsIntervals: Record<string, RatingIntervals> | null }>(async () => {
    if (target.kind !== 'unit') return { items: [], srsIntervals: null };
    const [items, gamification] = await Promise.all([
      fetchLessonItemsForMode(target.unitId, target.mode),
      progressApi.getGamification(),
    ]);
    const srsIntervals =
      gamification.srsEnabled && items.length > 0 ? await srsApi.getItemPreviews(items.map((i) => i.id)) : null;
    return { items, srsIntervals };
  }, [target.kind === 'unit' ? target.unitId : '', target.kind === 'unit' ? target.mode : '']);

  if (target.kind === 'kana') {
    const group = findKanaGroup(target.groupKey);
    if (!group) return <ErrorState message="Không tìm thấy nhóm chữ này." />;
    return <FlashcardDeckView items={group.cards} title={group.title} onBack={onBack} deckNoun="nhóm" itemNoun="thẻ" />;
  }

  if (unitDeck.loading) return <LoadingState />;
  if (unitDeck.error || !unitDeck.data) return <ErrorState message="Không tải được bộ flashcard." />;
  if (unitDeck.data.items.length === 0) return <ErrorState message="Bài này chưa có dữ liệu cho bộ thẻ này." />;

  const submit = async (itemId: string, rating: ReviewRating) => {
    try {
      return (await srsApi.submitItemReview(itemId, rating, new Date().toISOString())).intervals;
    } catch (error) {
      onToast('Không lưu được — thử lại');
      throw error;
    }
  };

  return (
    <FlashcardDeckView
      items={unitDeck.data.items}
      title={target.unitName}
      onBack={onBack}
      srs={unitDeck.data.srsIntervals ? { intervals: unitDeck.data.srsIntervals, submit } : undefined}
    />
  );
}
